import { ReactElement, useState } from "react";
import { Link } from "react-router-dom";
import { isActive, isSuspended } from "services/users";
import { isGraduated, isPilotCompleted, StudentStatus } from "services/students";
import { getUserFullName } from "services/util";
import {
  getFavoritesForInstructor,
  removeStudentFromFavorites,
} from "services/favorites";
import TalaeraRoutes from "components/routes/TalaeraRoutes";
import Action from "components/formElements/Action/Action";
import TalaeraSpinner from "components/TalaeraSpinner/TalaeraSpinner";
import RefreshableDataLoader from "../../../DataLoader/RefreshableDataLoader";
import trashIcon from "../../../../images/trash.svg";
import { CollapsablePanelSection } from "../CollapsablePanelSection/CollapsablePanelSection";
import { Student } from "./types";
import "./StudentListSidePanel.css";
import ConfirmationDialog from "../../../AppDialog/components/Dialog/components/ConfirmationDialog/ConfirmationDialog";
import useUser from "hooks/useUser";

interface StudentListSidePanelProps {
  location: Location;
}

export default function StudentListSidePanel(props: StudentListSidePanelProps) {
  let dataLoader: any;
  const refresh = () => {
    dataLoader && dataLoader.refresh();
  };
  return (
    <RefreshableDataLoader
      fetchData={getFavoritesForInstructor}
      fetchArgs={{ student_status: Object.values(StudentStatus).join(",") }}
      ref={c => {
        dataLoader = c;
      }}
    >
      <StudentListSidePanelInner {...props} refresh={refresh} />
    </RefreshableDataLoader>
  );
}

interface StudentListSidePanelInnerProps {
  data?: { list: Student[] };
  location: Location;
  refresh: () => void;
}

function StudentListSidePanelInner({
  data,
  location,
  refresh,
}: StudentListSidePanelInnerProps) {
  const sessionUser = useUser(true);

  const isActiveUserStatus = (userStatus: string) => {
    // For this sidepanel users are considered 'active' if they have 'active' or 'suspended'
    // user status.
    return isActive(userStatus) || isSuspended(userStatus);
  };

  const isInactiveStudentStatus = (studentStatus: string) => {
    // For this sidepanel students are considered 'inactive' if they have 'graduated'
    // or 'pilot-completed' student status.
    return isGraduated(studentStatus) || isPilotCompleted(studentStatus);
  };

  const isPrimaryInstructorFor = (user: Student) => {
    return user.primary_instructor && user.primary_instructor.id === sessionUser.id;
  };

  const isInactive = (user: Student) => {
    // Inactive student in terms of this panel is the one who "active" user and "inactive" student.
    return (
      isActiveUserStatus(user.status) && isInactiveStudentStatus(user.student.status)
    );
  };

  const isUserActive = (user: Student) => {
    // Active student in terms of this panel is the one who "active" user, "active" student and logged in user is primary instructor.
    return (
      isActiveUserStatus(user.status) &&
      !isInactiveStudentStatus(user.student.status) &&
      isPrimaryInstructorFor(user)
    );
  };

  const isTempStudent = (user: Student) => {
    // Active student in terms of this panel is the one who "active" user, "active" student and logged in user is not primary instructor.
    return (
      isActiveUserStatus(user.status) &&
      !isInactiveStudentStatus(user.student.status) &&
      !isPrimaryInstructorFor(user)
    );
  };

  return (
    <div className="student-list-side-panel">
      <CollapsablePanelSection
        sectionTitle="Active Students"
        list={data?.list.filter(isUserActive)}
        location={location}
        tagName={StudentListItem as (props: { [key: string]: any }) => ReactElement}
      />
      <CollapsablePanelSection
        sectionTitle="Temp Students"
        list={data?.list.filter(isTempStudent)}
        location={location}
        tagName={
          RemovableStudentListItem as (props: { [key: string]: any }) => ReactElement
        }
        refresh={refresh}
      />
      <CollapsablePanelSection
        sectionTitle="Inactive Students"
        list={data?.list.filter(isInactive)}
        location={location}
        tagName={StudentListItem as (props: { [key: string]: any }) => ReactElement}
      />
    </div>
  );
}

function StudentListItem(props: { location: Location; item: Student }) {
  const path = TalaeraRoutes.buildStudentDetailsPath(props.item.id);
  return (
    <li className={path === props.location.pathname ? "active" : ""}>
      <Link className="btn" to={path}>
        {getUserFullName(props.item.profile)}
      </Link>
    </li>
  );
}

interface RemovableStudentsListItemProps {
  refresh: () => void;
  location: Location;
  item: Student;
}

function RemovableStudentListItem({
  refresh,
  location,
  item,
}: RemovableStudentsListItemProps) {
  const [showSpinner, setShowSpinner] = useState(false);
  const [showDialog, setShowDialog] = useState(false);

  const handleDelete = (id: string) => {
    setShowSpinner(true);
    removeStudentFromFavorites(
      id,
      () => {
        refresh();
        setShowSpinner(false);
      },
      () => {
        refresh();
        setShowSpinner(false);
      }
    );
  };
  const path = TalaeraRoutes.buildStudentDetailsPath(item.id);

  return showSpinner ? (
    <TalaeraSpinner />
  ) : (
    <>
      <li className={`${path === location.pathname ? "active" : ""} student-list-item`}>
        <Link className="btn" to={path}>
          {getUserFullName(item.profile)}
        </Link>
        <Action
          className="btn"
          onClick={() => setShowDialog(true)}
          disabled={showSpinner}
        >
          <img className="trash-icon" src={trashIcon} alt="Delete" />
        </Action>
      </li>
      <ConfirmationDialog
        showDialog={showDialog}
        title="Remove from temporary list?"
        onClose={() => setShowDialog(false)}
        actions={[
          {
            name: "Cancel",
            handler: () => setShowDialog(false),
          },
          {
            name: "Remove",
            handler: () => handleDelete(item.id),
          },
        ]}
      >
        Do you want to remove this student from your Temp Students list?
        <br />
        <br />
      </ConfirmationDialog>
    </>
  );
}
